// SuppliersPage.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { readSessionSort, writeSessionSort, clearSessionSort } from '../utils/sessionSort';
import { formatDistanceToNow, format as formatDate } from 'date-fns';
import { he } from 'date-fns/locale';
import {
  DataGridPro,
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SupplierCreateDialog from './dialogs/SupplierCreateDialog';
import SupplierPaymentDialog from './dialogs/SupplierPaymentDialog';
import SupplierCreditDialog from './dialogs/SupplierCreditDialog';
import SupplierPurchaseDialog from './dialogs/SupplierPurchaseDialog';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar from '../components/ResponsiveToolbar';

// ==================== THEME ====================
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// ==================== CUSTOM TOOLBAR ====================
function CustomToolbar({ isExporting, setIsExporting, navigate, ordering }) {
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const response = await API.get('/suppliers/export-excel/', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ספקים_ייצוא.xlsx');
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
      alert('שגיאה ביצוא קובץ - נסה שוב');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={handleExportExcel}
        disabled={isExporting}
      >
        {isExporting ? <CircularProgress size={24} /> : 'אקסל'}
      </Button>
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={() => {
          const params = new URLSearchParams();
          if (ordering) params.append('ordering', ordering);
          const qs = params.toString();
          navigate('/suppliers/minimal-print' + (qs ? '?' + qs : ''));
        }}
      >
        הדפסה
      </Button>
    </GridToolbarContainer>
  );
}

// ==================== FETCH SUPPLIERS ====================
export async function fetchSuppliers(ordering) {
  const response = await API.get('/suppliers/', {
    params: ordering ? { ordering } : undefined,
  });
  return response.data;
}

// Default ordering for the suppliers list — most-recently-interacted first.
// Backed by GetSuppliersView's `last_interaction_at` annotation.
const DEFAULT_SUPPLIERS_ORDERING = '-last_interaction_at';

// Convert an API ordering string to a DataGridPro sortModel entry.
// Always returns a populated sortModel so the column-header arrow stays
// in sync — including for the default sort.
const getSortModelFromOrdering = (ordering) => {
  if (!ordering) return [];
  const isDesc = ordering.startsWith('-');
  const field = isDesc ? ordering.slice(1) : ordering;
  return [{ field, sort: isDesc ? 'desc' : 'asc' }];
};

// Convert a DataGridPro sortModel back to an API ordering string.
const getOrderingFromSortModel = (sortModel) => {
  if (!sortModel || sortModel.length === 0) {
    return DEFAULT_SUPPLIERS_ORDERING;
  }
  const { field, sort } = sortModel[0];
  return sort === 'desc' ? `-${field}` : field;
};

const SuppliersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const queryClient = useQueryClient();
  const role = userProfile?.role;

  // Get page config for access control
  const pageConfig = userProfile?.ui_config?.pages_configs?.SuppliersPage || {};
  const allowedRoles = pageConfig.allowed_roles || ['manager', 'employee'];
  const createRoles = pageConfig.create_roles || ['manager'];
  const detailPageRoles = pageConfig.detail_page_roles || ['manager', 'employee'];

  // Dialog state
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedActionSupplier, setSelectedActionSupplier] = useState(null);

  // Quick actions menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuSupplier, setActionMenuSupplier] = useState(null);

  const handleActionMenuOpen = (event, supplier) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuSupplier(supplier);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuSupplier(null);
  };

  const handleQuickAction = (type) => {
    setSelectedActionSupplier(actionMenuSupplier);
    handleActionMenuClose();
    if (type === 'payment') setPaymentDialogOpen(true);
    else if (type === 'credit') setCreditDialogOpen(true);
    else if (type === 'purchase') setPurchaseDialogOpen(true);
  };

  const handleActionSuccess = () => {
    // Invalidate ALL ['suppliers', ...] queries so any view of the supplier
    // list refetches after a payment / credit / purchase. Critical now that
    // the list sorts by -last_interaction_at by default — the just-
    // transacted supplier must jump to position #1 without a manual reload.
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    refetch();
  };

  // User-specific storage keys
  const userId = userProfile?.username || 'unknown';
  const GRID_STATE_KEY = `suppliers-datagrid-state-${userId}`;  // localStorage (density, columns)
  const SORT_OVERRIDE_KEY = `suppliers-sort-override-${userId}`;  // sessionStorage + 24h TTL

  // API ref for DataGridPro
  const apiRef = useGridApiRef();

  // Sort lives in sessionStorage (24h TTL) so the recency default reasserts
  // itself on new tabs / new days. Default = -last_interaction_at.
  const [ordering, setOrdering] = useState(
    () => readSessionSort(SORT_OVERRIDE_KEY) || DEFAULT_SUPPLIERS_ORDERING
  );

  useEffect(() => {
    if (ordering === DEFAULT_SUPPLIERS_ORDERING) {
      clearSessionSort(SORT_OVERRIDE_KEY);
    } else {
      writeSessionSort(SORT_OVERRIDE_KEY, ordering);
    }
  }, [ordering, SORT_OVERRIDE_KEY]);

  // Load initial grid state from localStorage. Sorting is NOT read from
  // localStorage anymore — it lives in sessionStorage.
  // Sorting is intentionally NOT in initialState — it's driven by the
  // controlled `sortModel` prop. Mixing the two causes the column-header
  // arrow to freeze on the snapshot taken at mount.
  const initialGridState = React.useMemo(() => {
    let density = 'comfortable';
    try {
      const stateJSON = localStorage.getItem(GRID_STATE_KEY);
      if (stateJSON) {
        const parsed = JSON.parse(stateJSON);
        density = parsed.density || 'comfortable';
      }
    } catch (e) {
      console.error("Failed to load grid state from localStorage", e);
    }
    return { density };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GRID_STATE_KEY]);

  const handleStateChange = React.useCallback(() => {
    if (apiRef.current) {
      try {
        const state = apiRef.current.exportState();
        // Sort is NOT persisted here — it lives in sessionStorage.
        const stateToSave = {
          density: state.density,
          columns: state.columns,
        };
        localStorage.setItem(GRID_STATE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Failed to save grid state:', error);
      }
    }
  }, [apiRef, GRID_STATE_KEY]);

  const handleSortModelChange = useCallback((newSortModel) => {
    setOrdering(getOrderingFromSortModel(newSortModel));
  }, []);

  // Memoized so DataGridPro doesn't receive a fresh array each render.
  const sortModel = useMemo(() => getSortModelFromOrdering(ordering), [ordering]);

  // Mobile sort dropdown options — same shape as ClientsListPageCached
  const mobileSortOptions = useMemo(() => ([
    { key: 'last_interaction_at_desc', label: 'פעילות אחרונה (חדש→ישן)', field: 'last_interaction_at', direction: 'desc' },
    { key: 'SupplierName_asc',         label: 'שם ספק (א-ת)',           field: 'SupplierName',        direction: 'asc'  },
    { key: 'SupplierName_desc',        label: 'שם ספק (ת-א)',           field: 'SupplierName',        direction: 'desc' },
    { key: 'CreatedAt_desc',           label: 'נוצר (חדש→ישן)',         field: 'CreatedAt',           direction: 'desc' },
    { key: 'Balance_desc',             label: 'יתרה - מהגבוה לנמוך',    field: 'Balance',             direction: 'desc' },
    { key: 'Balance_asc',              label: 'יתרה - מהנמוך לגבוה',    field: 'Balance',             direction: 'asc'  },
  ]), []);

  const selectedSortKey = useMemo(() => {
    if (!ordering) return 'last_interaction_at_desc';
    const isDesc = ordering.startsWith('-');
    const field = isDesc ? ordering.slice(1) : ordering;
    const key = `${field}_${isDesc ? 'desc' : 'asc'}`;
    return mobileSortOptions.some((o) => o.key === key) ? key : 'last_interaction_at_desc';
  }, [ordering, mobileSortOptions]);

  const handleMobileSortChange = useCallback((newKey) => {
    const opt = mobileSortOptions.find((o) => o.key === newKey);
    if (!opt) return;
    setOrdering(opt.direction === 'desc' ? `-${opt.field}` : opt.field);
  }, [mobileSortOptions]);

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  // ==================== REACT QUERY SETUP ====================
  const {
    data: suppliersData = [],
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['suppliers', ordering],
    queryFn: () => fetchSuppliers(ordering),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // ==================== Filtering Logic ====================
  const filteredSuppliers = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return suppliersData;
    }

    const lowerSearch = searchTerm.toLowerCase().trim();

    return suppliersData.filter((supplier) => {
      // Build the exact display string that appears in autocomplete
      const supplierDisplay = `${supplier.SupplierName} ${supplier.SupplierBusinessID ? `(${supplier.SupplierBusinessID})` : ''}`.toLowerCase();
      
      // Check if the search matches the exact display string (for autocomplete selection)
      if (supplierDisplay === lowerSearch) {
        return true;
      }
      
      // Also check individual fields for partial/flexible search when typing
      const searchableText = `${supplier.SupplierName || ''} ${supplier.SupplierBusinessID || ''} ${supplier.ContactPerson || ''}`.toLowerCase();
      
      // For typed searches, check if any part matches
      return searchableText.includes(lowerSearch) || supplierDisplay.includes(lowerSearch);
    });
  }, [suppliersData, searchTerm]);

  // ==================== Column Definitions (Only 3 columns) ====================
  const columns = useMemo(() => [
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleActionMenuOpen(e, params.row)}
          aria-label="פעולות מהירות"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: 'SupplierName',
      headerName: 'שם הספק',
      flex: 1,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params?.row || {};
        const canNavigate = detailPageRoles.includes(role);

        if (canNavigate) {
          return (
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate(`/supplier/${row.SupplierID}`)}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {row.SupplierName}
            </Button>
          );
        } else {
          return (
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {row.SupplierName}
            </Typography>
          );
        }
      },
    },
    {
      field: 'SupplierBusinessID',
      headerName: 'ח.פ./ע.מ.',
      flex: 1,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.row.SupplierBusinessID || '-',
    },
    {
      field: 'Currency',
      headerName: 'מטבע',
      width: 110,
      mobile: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const currency = params.row?.Currency || 'ILS';
        const currencyInfo = {
          ILS: { symbol: '₪', label: 'שקל', color: 'default' },
          USD: { symbol: '$', label: 'דולר', color: 'info' },
          EUR: { symbol: '€', label: 'אירו', color: 'secondary' },
        };
        const info = currencyInfo[currency] || { symbol: currency, label: currency, color: 'default' };
        return (
          <Chip
            label={`${info.symbol} ${info.label}`}
            size="small"
            variant={currency === 'ILS' ? 'filled' : 'outlined'}
            color={info.color}
          />
        );
      },
    },
    {
      field: 'Balance',
      headerName: 'יתרה',
      flex: 1,
      minWidth: 160,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => parseFloat(row.Balance || '0'),
      renderCell: (params) => {
        const balance = parseFloat(params.row?.Balance || '0');
        const currency = params.row?.Currency;
        const isNonILS = currency && currency !== 'ILS';
        const foreignBalance = parseFloat(params.row?.ForeignBalance || '0');

        const primaryFormatted = isNonILS
          ? new Intl.NumberFormat('he-IL', { style: 'currency', currency }).format(foreignBalance)
          : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(balance);
        const displayBalance = isNonILS ? foreignBalance : balance;

        return (
          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: displayBalance > 0 ? 'error.main' : displayBalance < 0 ? 'success.main' : 'text.primary',
                fontWeight: 'bold',
                lineHeight: 1.3,
              }}
            >
              {primaryFormatted}
            </Typography>
            {isNonILS && (
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(balance)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'last_interaction_at',
      headerName: 'פעילות אחרונה',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      sortable: true,
      type: 'dateTime',
      valueGetter: (value, row) => {
        const raw = row?.last_interaction_at;
        return raw ? new Date(raw) : null;
      },
      renderCell: (params) => {
        const d = params.value;
        if (!d) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.9rem' }}>
                —
              </Typography>
            </Box>
          );
        }
        const relative = formatDistanceToNow(d, { addSuffix: true, locale: he });
        const absolute = formatDate(d, 'dd/MM/yyyy HH:mm');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Tooltip title={absolute} arrow placement="top" enterDelay={300}>
              <Typography variant="body2" sx={{ fontSize: '0.95rem', textAlign: 'center' }}>
                {relative}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [role, detailPageRoles, navigate]);

  // ==================== Mobile Card Renderer ====================
  const supplierCardRenderer = (row) => {
    const balance = parseFloat(row?.Balance || '0');
    const formatted = new Intl.NumberFormat('he-IL', {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(balance);

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.SupplierName}
          </Typography>
          {row.SupplierBusinessID && (
            <Typography variant="body2" color="text.secondary">
              ח.פ./ע.מ.: {row.SupplierBusinessID}
            </Typography>
          )}
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            color: balance > 0 ? 'error.main' : balance < 0 ? 'success.main' : 'text.primary',
            flexShrink: 0,
          }}
        >
          {formatted}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => handleActionMenuOpen(e, row)}
          aria-label="פעולות מהירות"
          sx={{ flexShrink: 0 }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 2 } }}>
          <Typography variant="h3" sx={{ mb: 3 }}>
            ספקים
          </Typography>

          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  רשימת ספקים
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              {/* Search & Create-Button row */}
              <ResponsiveToolbar>
              <Autocomplete
                freeSolo
                options={suppliersData}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  // This is how the option will be displayed
                  return `${option.SupplierName} ${option.SupplierBusinessID ? `(${option.SupplierBusinessID})` : ''}`;
                }}
                inputValue={searchTerm}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue);
                }}
                onChange={(event, newValue) => {
                  if (newValue === null || newValue === undefined) {
                    setSearchTerm('');
                  } else if (typeof newValue === 'string') {
                    setSearchTerm(newValue);
                  } else if (typeof newValue === 'object') {
                    // When selecting from dropdown, use the FULL display string
                    // This ensures we can differentiate between suppliers with same name
                    const fullDisplay = `${newValue.SupplierName} ${newValue.SupplierBusinessID ? `(${newValue.SupplierBusinessID})` : ''}`;
                    setSearchTerm(fullDisplay);
                  }
                }}
                isOptionEqualToValue={(option, value) => 
                  option?.SupplierID === value?.SupplierID
                }
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="חיפוש לפי שם, ח.פ. או איש קשר"
                    placeholder="הקלד שם ספק, ח.פ. או איש קשר"
                    size="small"
                    sx={{ width: { xs: '100%', md: 320 }, minWidth: { xs: 0, md: 320 } }}
                  />
                )}
              />

                {/* Mobile-only sort dropdown — desktop uses DataGridPro column-header sort */}
                <TextField
                  select
                  label="מיון"
                  size="small"
                  value={selectedSortKey}
                  onChange={(e) => handleMobileSortChange(e.target.value)}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    gridColumn: { xs: '1 / -1', md: 'auto' },
                    width: { xs: '100%', md: 250 },
                    minWidth: { xs: 0, md: 250 },
                  }}
                >
                  {mobileSortOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Button variant="outlined" onClick={() => {
                  const params = new URLSearchParams();
                  if (ordering) params.append('ordering', ordering);
                  const qs = params.toString();
                  navigate('/suppliers/minimal-print' + (qs ? '?' + qs : ''));
                }} sx={{ direction: 'ltr' }}>
                  הדפסה
                </Button>
                {createRoles.includes(role) && (
                  <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
                    הוסף ספק חדש
                  </Button>
                )}
              </ResponsiveToolbar>

              {/* DataGrid */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                  }}
                >
                  <ResponsiveDataView
                    rows={filteredSuppliers}
                    columns={columns}
                    cardRenderer={supplierCardRenderer}
                    onRowClick={(row) => navigate(`/supplier/${row.SupplierID}`)}
                    getRowId={(row) => row.SupplierID}
                    emptyMessage="אין תוצאות"
                    getRowHeight={() => 'auto'}
                    apiRef={apiRef}
                    density="comfortable"
                    autoHeight
                    pageSizeOptions={[25, 50, 100]}
                    initialState={initialGridState}
                    onStateChange={handleStateChange}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    sortingOrder={['asc', 'desc']}
                    pagination
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    slots={{
                      toolbar: () => (
                        <CustomToolbar
                          isExporting={isExporting}
                          setIsExporting={setIsExporting}
                          navigate={navigate}
                          ordering={ordering}
                        />
                      ),
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          אין תוצאות
                        </Box>
                      ),
                    }}
                    sx={{
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',
                        lineHeight: '1.2',
                        overflow: 'visible',
                      },
                      '.MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '1rem',
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>

        {/* CREATE DIALOG */}
        {openCreateDialog && (
          <SupplierCreateDialog
            open={openCreateDialog}
            onClose={handleCloseCreateDialog}
            userProfile={userProfile}
          />
        )}

        {/* QUICK ACTIONS MENU */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleQuickAction('payment')}>תשלום</MenuItem>
          <MenuItem onClick={() => handleQuickAction('credit')}>זיכוי</MenuItem>
          <MenuItem onClick={() => handleQuickAction('purchase')}>רכישה</MenuItem>
        </Menu>

        {/* QUICK ACTION DIALOGS */}
        {paymentDialogOpen && selectedActionSupplier && (
          <SupplierPaymentDialog
            open={paymentDialogOpen}
            onClose={() => { setPaymentDialogOpen(false); setSelectedActionSupplier(null); }}
            supplier={selectedActionSupplier}
            onSuccess={handleActionSuccess}
          />
        )}
        {creditDialogOpen && selectedActionSupplier && (
          <SupplierCreditDialog
            open={creditDialogOpen}
            onClose={() => { setCreditDialogOpen(false); setSelectedActionSupplier(null); }}
            supplier={selectedActionSupplier}
            onSuccess={handleActionSuccess}
          />
        )}
        {purchaseDialogOpen && selectedActionSupplier && (
          <SupplierPurchaseDialog
            open={purchaseDialogOpen}
            onClose={() => { setPurchaseDialogOpen(false); setSelectedActionSupplier(null); }}
            supplier={selectedActionSupplier}
            onSuccess={handleActionSuccess}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default SuppliersPage;